import React from "react";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { motion } from "framer-motion";

const ContactUs = () => {
  return (
    <div className=" min-h-screen bg-slate-50 font-poppins ">
      {/* Hero Header Banner */}
      <div className="relative bg-gradient-to-br from-[#00261E] via-[#0B5647] to-[#00261E] text-white overflow-hidden pb-10 pt-[70px] md:pt-[180px] px-5 md:px-12 select-none">
        {/* Background overlay shapes (Subtle geometric diagonal shapes matching the footer) */}
        <div className="absolute inset-0 z-0 pointer-events-none hidden md:block">
          {/* Large diagonal triangle dividing from top-left to bottom-right */}
          <div
            className="absolute inset-0 bg-black/[0.08]"
            style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
          ></div>
          {/* Sleek bottom-right diagonal accent triangle */}
          <div
            className="absolute bottom-0 right-0 w-full h-[60%] bg-white/[0.03]"
            style={{ clipPath: "polygon(100% 100%, 0 100%, 100% 0)" }}
          ></div>
          {/* Distinct top-right diagonal triangle */}
          <div
            className="absolute top-0 right-0 w-1/2 h-full bg-white/[0.04]"
            style={{ clipPath: "polygon(100% 0, 20% 0, 100% 80%)" }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center max-w-[800px] mx-auto">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-brandLime/10 text-brandLime border border-brandLime/20 px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2.5"
          >
            Get in touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-5xl font-black tracking-tight mb-3"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-300 text-xs md:text-sm font-light leading-relaxed max-w-lg"
          >
            Have any questions or need booking support? We are here to help you 24/7.
            Reach out and we'll get back to you as soon as possible.
          </motion.p>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="max-w-[850px] mx-auto px-4 py-8 md:py-12">


        {/* Grid of Contact Cards */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.08,
              },
            },
          }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {/* Call Card */}
          <motion.a
            href="tel:00000000000"
            variants={{
              hidden: { opacity: 0, y: 15 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
            }}
            whileHover={{ scale: 1.02 }}
            className="group relative overflow-hidden bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-500 text-center flex flex-col items-center"
          >
            {/* Hover Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00261E] to-[#0B5647] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Icon Container */}
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-[#0c9e82] group-hover:bg-brandLime group-hover:text-primary group-hover:rotate-[360deg] transition-all duration-500 shadow-sm mb-4 shrink-0">
                <FaPhoneAlt size={20} />
              </div>

              <h3 className="text-[10px] md:text-xs font-bold text-gray group-hover:text-brandLime/90 transition-colors duration-300 uppercase tracking-wider">
                Call Us On
              </h3>

              <p className="text-lg md:text-xl font-extrabold text-primary group-hover:text-white transition-colors duration-300 mt-2 leading-tight">
                00000000000
              </p>

              <p className="text-[11px] md:text-xs text-slate-500 group-hover:text-gray-300 transition-colors duration-300 mt-2 font-medium leading-relaxed max-w-[220px]">
                Available 24 hours a day, 7 days a week.
              </p>
            </div>
          </motion.a>

          {/* Email Card */}
          <motion.a
            href="mailto:info@dummy.co.uk"
            variants={{
              hidden: { opacity: 0, y: 15 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
            }}
            whileHover={{ scale: 1.02 }}
            className="group relative overflow-hidden bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-500 text-center flex flex-col items-center"
          >
            {/* Hover Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00261E] to-[#0B5647] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Icon Container */}
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-[#0c9e82] group-hover:bg-brandLime group-hover:text-primary group-hover:rotate-[360deg] transition-all duration-500 shadow-sm mb-4 shrink-0">
                <FaEnvelope size={20} />
              </div>

              <h3 className="text-[10px] md:text-xs font-bold text-gray group-hover:text-brandLime/90 transition-colors duration-300 uppercase tracking-wider">
                Email Us
              </h3>

              <p className="text-lg md:text-xl font-extrabold text-primary group-hover:text-white transition-colors duration-300 mt-2 break-all leading-tight">
                info@dummy.co.uk
              </p>

              <p className="text-[11px] md:text-xs text-slate-500 group-hover:text-gray-300 transition-colors duration-300 mt-2 font-medium leading-relaxed max-w-[220px]">
                We typically reply within 24 business hours.
              </p>
            </div>
          </motion.a>

          {/* Location Card */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 15 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
            }}
            whileHover={{ scale: 1.02 }}
            className="group relative overflow-hidden bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-500 text-center flex flex-col items-center"
          >
            {/* Hover Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00261E] to-[#0B5647] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Icon Container */}
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-[#0c9e82] group-hover:bg-brandLime group-hover:text-primary group-hover:rotate-[360deg] transition-all duration-500 shadow-sm mb-4 shrink-0">
                <FaMapMarkerAlt size={20} />
              </div>

              <h3 className="text-[10px] md:text-xs font-bold text-gray group-hover:text-brandLime/90 transition-colors duration-300 uppercase tracking-wider">
                Our Location
              </h3>

              <p className="text-lg md:text-xl font-extrabold text-primary group-hover:text-white transition-colors duration-300 mt-2 leading-tight">
                London, United Kingdom
              </p>

              <p className="text-[11px] md:text-xs text-slate-500 group-hover:text-gray-300 transition-colors duration-300 mt-2 font-medium leading-relaxed max-w-[220px]">
                Serving all major UK airport terminals.
              </p>
            </div>
          </motion.div>

          {/* Support Hours Card */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 15 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
            }}
            whileHover={{ scale: 1.02 }}
            className="group relative overflow-hidden bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-500 text-center flex flex-col items-center"
          >
            {/* Hover Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00261E] to-[#0B5647] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Icon Container */}
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-[#0c9e82] group-hover:bg-brandLime group-hover:text-primary group-hover:rotate-[360deg] transition-all duration-500 shadow-sm mb-4 shrink-0">
                <FaClock size={20} />
              </div>

              <h3 className="text-[10px] md:text-xs font-bold text-gray group-hover:text-brandLime/90 transition-colors duration-300 uppercase tracking-wider">
                Working Hours
              </h3>

              <p className="text-lg md:text-xl font-extrabold text-primary group-hover:text-white transition-colors duration-300 mt-2 leading-tight">
                24 / 7 / 365 Days
              </p>

              <p className="text-[11px] md:text-xs text-slate-500 group-hover:text-gray-300 transition-colors duration-300 mt-2 font-medium leading-relaxed max-w-[220px]">
                Monitoring and customer support live.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUs;
